import React, { useCallback } from 'react';
import { TgsArticleImage } from '~/common/components/ArticleImage';
import type { BghArticle } from '~/common/types/bghArticle';
import { useCatalogBghArticle } from '~/features/catalog/hooks/useCatalogBghArticle';
import { ArticlePrice } from '~/features/catalog/components/article/ArticlePrice';
import { cn } from '~/common/utils/styleUtil';
import { ArticleSecondaryText } from '~/features/catalog/components/article/ArticleSecondaryText';
import { AddToCart } from '~/features/catalog/components/AddToCart';
import { ArticleSellUnit } from '~/features/catalog/components/article/ArticleSellUnit';
import ArticleInfoIcons from '~/features/catalog/components/ArticleInfoIcons.tsx';
import { useScreenSize } from '~/common/hooks/useScreenSize.ts';

export function QuickSearchBghArticleItem({
  article,
  onClick,
  className,
  isAuthenticated,
  selectedArticleNumber
}: {
  article: BghArticle;
  onClick: (artNo: string) => void;
  className?: string;
  isAuthenticated: boolean;
  selectedArticleNumber?: string;
}) {
  const { isMobile } = useScreenSize();

  const catalogArticle = useCatalogBghArticle(article, selectedArticleNumber);

  const handleArticleChange = useCallback(
    (artNo: string) => {
      catalogArticle.setSelectedArticleNumber(artNo);
    },
    [catalogArticle]
  );

  const handleArticleClick = useCallback(() => {
    onClick(catalogArticle.articleNumber);
  }, [onClick, catalogArticle.articleNumber]);

  const handleInteractionClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleInteractionKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  return isMobile ? (
    <article
      className={cn(
        'group border-b desktop:border-b-0 border-r-0 p-4 xl:pl-4 hover:bg-neutral-50 h-full flex flex-col',
        className
      )}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        id={catalogArticle.articleNumber}
        onClick={handleArticleClick}
        className="flex flex-col flex-nowrap gap-4 cursor-pointer items-stretch cursor-pointer h-full"
      >
        <div className="flex gap-4 item-center">
          <TgsArticleImage
            articleNumber={catalogArticle.articleNumber}
            celumId={catalogArticle.celumId}
            alt={catalogArticle.description}
            size={'small'}
          />
          <div className={'flex flex-col'}>
            <ArticlePrice
              price={catalogArticle.price}
              oldPrice={catalogArticle.oldPrice}
              isAction={catalogArticle.isAction}
              actionValidTo={catalogArticle.actionValidTo}
              unitText={catalogArticle.unitText}
              className={'lg:mx-4 xl:mx-8'}
              minimize={true}
              isHit={catalogArticle.isHit}
            />
            <div className="grow">
              <div className="flex flex-col justify-center h-full">
                <h2
                  className={cn(
                    'text-base font-bold line-clamp-2',
                    catalogArticle.isAction && !catalogArticle.isHit && 'text-primary'
                  )}
                >
                  {catalogArticle.description}
                </h2>
                <ArticleSecondaryText
                  articleNumber={catalogArticle.articleNumber}
                  rohstoffHerkunft={catalogArticle.rohstoffHerkunft}
                  brand={catalogArticle.brand}
                  lineClamp={1}
                />
                <ArticleInfoIcons article={catalogArticle} className={'flex py-1'} isAuthenticated={isAuthenticated} />
              </div>
            </div>
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="grow-0 shrink-0" onClick={handleInteractionClick} onKeyDown={handleInteractionKeyDown}>
          <div className={'flex flex-row items-center justify-end h-full'}>
            <div>
              {isAuthenticated && (
                <>
                  <ArticleSellUnit
                    selectedArticle={catalogArticle}
                    allArticles={catalogArticle.allArticles}
                    onArticleChange={changeInfo => handleArticleChange(changeInfo.articleNumber.toString())}
                    className={'text-right'}
                  />
                  <div className="flex">
                    <AddToCart
                      articleNumber={catalogArticle.articleNumber}
                      articleDescriptionText={catalogArticle.description}
                      mainArticleId={catalogArticle.mainArticleId}
                      isExternalArticle={false}
                      isProfitPlus={catalogArticle.isProfitPlus}
                      calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
                      quantity={catalogArticle.quantity}
                      onQuantityChange={catalogArticle.setQuantity}
                      isAuthenticated={isAuthenticated}
                      hideCart={catalogArticle.hideCart}
                      className={'min-w-[200px] justify-end'}
                      hideTemplateButton={true}
                      disableAmountInput={catalogArticle.hideCart}
                      hideAddToTemplate={catalogArticle.isInactive}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  ) : (
    <article
      className={cn(
        'group border-b desktop:border-b-0 border-r-0 p-4 xl:pl-4 hover:bg-neutral-50 h-full flex flex-col',
        className
      )}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        id={catalogArticle.articleNumber}
        onClick={handleArticleClick}
        className="flex flex-row flex-nowrap gap-4 cursor-pointer items-stretch cursor-pointer h-full"
      >
        <div className="flex">
          <TgsArticleImage
            articleNumber={catalogArticle.articleNumber}
            celumId={catalogArticle.celumId}
            alt={catalogArticle.description}
            size={'small'}
          />
        </div>
        <div className="grow">
          <div className="flex flex-col justify-center h-full">
            <ArticlePrice
              price={catalogArticle.price}
              oldPrice={catalogArticle.oldPrice}
              isAction={catalogArticle.isAction}
              actionValidTo={catalogArticle.actionValidTo}
              unitText={catalogArticle.unitText}
              //className={'lg:mx-4 xl:mx-8'}
              minimize={true}
              isHit={catalogArticle.isHit}
            />
            <h2
              className={cn(
                'text-base font-bold line-clamp-2',
                catalogArticle.isAction && !catalogArticle.isHit && 'text-primary'
              )}
            >
              {catalogArticle.description}
            </h2>
            <ArticleSecondaryText
              articleNumber={catalogArticle.articleNumber}
              rohstoffHerkunft={catalogArticle.rohstoffHerkunft}
              brand={catalogArticle.brand}
              lineClamp={1}
            />
            <ArticleInfoIcons article={catalogArticle} className={'flex py-1'} isAuthenticated={isAuthenticated} />
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="grow-0 shrink-0" onClick={handleInteractionClick} onKeyDown={handleInteractionKeyDown}>
          <div className={'flex flex-row items-center h-full'}>
            <div>
              {isAuthenticated && (
                <>
                  <div>
                    <ArticleSellUnit
                      selectedArticle={catalogArticle}
                      allArticles={catalogArticle.allArticles}
                      onArticleChange={changeInfo => handleArticleChange(changeInfo.articleNumber.toString())}
                      className={'text-right'}
                      renderAsPortal={false}
                    />
                  </div>
                  <div className="flex">
                    <AddToCart
                      articleNumber={catalogArticle.articleNumber}
                      articleDescriptionText={catalogArticle.description}
                      mainArticleId={catalogArticle.mainArticleId}
                      isExternalArticle={false}
                      isProfitPlus={catalogArticle.isProfitPlus}
                      calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
                      quantity={catalogArticle.quantity}
                      onQuantityChange={catalogArticle.setQuantity}
                      isAuthenticated={isAuthenticated}
                      hideCart={catalogArticle.hideCart}
                      className={'min-w-[200px] justify-end'}
                      hideTemplateButton={true}
                      disableAmountInput={catalogArticle.hideCart}
                      hideAddToTemplate={catalogArticle.isInactive}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
